import React from "react";
import { Grid, Box, Typography, Stack, alpha, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";
import {
  LocationCity as LocationCityIcon,
  PinDrop as PinDropIcon,
  Public as PublicIcon,
  Map as MapIcon,
  History as HistoryIcon,
  AppRegistration as AppRegistrationIcon,
  PersonAdd as PersonAddIcon,
  ContactPage as ContactPageIcon,
  TravelExplore as TravelExploreIcon,
  Room as RoomIcon,
} from "@mui/icons-material";
import map from "../../../assets/illustrations/map.png";
import calendarPastel from "../../../assets/illustrations/3d_calendar_pastel.svg";
import { getWaveSvg } from "../../../utils/common";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const SectionTitle = ({ title, icon: Icon, color = "primary", centered = false }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;
  const darkColor = theme.palette[color]?.dark || theme.palette.primary.dark;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: centered ? "center" : "flex-start",
        gap: 2,
        mb: 3,
        p: 1.5,
        width: "100%",
        borderRadius: "12px",
        background: isDarkMode
          ? "rgba(255, 255, 255, 0.08)"
          : `linear-gradient(135deg, ${alpha(mainColor, 0.3)} 0%, ${alpha(mainColor, 0.1)} 100%)`,
        border: `1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.12)" : alpha(mainColor, 0.4)}`,
        boxShadow: isDarkMode
          ? "0 4px 12px rgba(0, 0, 0, 0.2)"
          : `0 4px 12px ${alpha(mainColor, 0.15)}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 38,
          height: 38,
          borderRadius: "10px",
          bgcolor: isDarkMode ? "rgba(255, 255, 255, 0.12)" : "white",
          boxShadow: `0 4px 10px ${alpha(mainColor, 0.2)}`,
        }}
      >
        <Icon sx={{ fontSize: "1.3rem", color: mainColor }} />
      </Box>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 900,
          letterSpacing: "0.02em",
          color: isDarkMode ? "rgba(255, 255, 255, 0.95)" : darkColor,
          fontSize: "0.95rem",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const PropertyTile = ({ label, value, icon: Icon, highlight, color = "primary" }) => {
  const theme = useTheme();
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "18px",
        bgcolor: alpha(theme.palette.background.paper, 0.4),
        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "4px",
          height: "0%",
          bgcolor: mainColor,
          transition: "height 0.3s ease",
          borderRadius: "0 4px 4px 0",
        },
        "&:hover": {
          bgcolor: alpha(theme.palette.background.paper, 1),
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.04)",
          borderColor: alpha(mainColor, 0.2),
          "&::before": {
            height: "100%",
          },
          "& .tile-icon": {
            color: mainColor,
            transform: "scale(1.1)",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.65rem",
          }}
        >
          {label}
        </Typography>
        {Icon && (
          <Icon
            className="tile-icon"
            sx={{
              fontSize: "1rem",
              color: alpha(theme.palette.text.secondary, 0.3),
              transition: "all 0.3s ease",
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          color: highlight ? mainColor : "text.primary",
          fontWeight: 900,
          fontSize: "0.95rem",
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {value || "---"}
      </Typography>
    </Box>
  );
};

export const LocationWidget = ({ user }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: "28px",
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "background.paper" : "white"),
        border: `2px solid ${alpha(theme.palette.info.main, 0.4)}`,
        boxShadow: `0 12px 40px ${alpha(theme.palette.info.main, 0.15)}`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          boxShadow: `0 16px 50px ${alpha(theme.palette.info.main, 0.25)}`,
          border: `2px solid ${theme.palette.info.main}`,
          transform: "translateY(-4px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "3px",
          background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)`,
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        textAlign: "left",
        position: "relative",
        overflow: "hidden",
        animation: `${fadeInUp} 0.6s ease-out 0.3s forwards`,
      }}
    >
      <SectionTitle title="Location Details" icon={TravelExploreIcon} color="info" />

      <Grid container spacing={0} sx={{ flexGrow: 1 }}>
        <Grid
          item
          xs={5}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "160px",
              borderRadius: "24px",
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "white",
            }}
          >
            <Box
              component="img"
              src={map}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "left center",
                transform: "scale(1.1)",
                mixBlendMode: "multiply",
                opacity: 0.9,
                filter: "brightness(0.92) contrast(1.1)",
                animation: `${float} 6s ease-in-out infinite`,
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={7} sx={{ pl: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <Box
              sx={{
                pb: 2,
                mb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: "text.secondary",
                  fontSize: "0.65rem",
                  mb: 0.5,
                  display: "block",
                  letterSpacing: "0.05em",
                }}
              >
                PRIMARY ADDRESS
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "primary.main", lineHeight: 1.4 }}
              >
                {user.address}
              </Typography>
            </Box>

            <Grid container sx={{ mb: 2 }}>
              <Grid
                item
                xs={6}
                sx={{
                  pr: 2,
                  borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  CITY
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {user.city || "---"}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ pl: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  PINCODE
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {user.pinCode || "---"}
                </Typography>
              </Grid>
            </Grid>

            <Grid
              container
              sx={{ pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
            >
              <Grid
                item
                xs={6}
                sx={{
                  pr: 2,
                  borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  STATE
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {user.state || "---"}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ pl: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    mb: 0.5,
                    display: "block",
                    letterSpacing: "0.05em",
                  }}
                >
                  COUNTRY
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {user.country || "---"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export const AccountHistoryWidget = ({ user }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  return (
    <Box
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: "28px",
        bgcolor: isDarkMode ? "background.paper" : "white",
        border: `2px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
        boxShadow: `0 12px 40px ${alpha(theme.palette.secondary.main, 0.15)}`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          boxShadow: `0 16px 50px ${alpha(theme.palette.secondary.main, 0.25)}`,
          border: `2px solid ${theme.palette.secondary.main}`,
          transform: "translateY(-4px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "3px",
          background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
        },
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        textAlign: "left",
        position: "relative",
        overflow: "hidden",
        animation: `${fadeInUp} 0.6s ease-out 0.4s forwards`,
      }}
    >
      <SectionTitle title="Account History" icon={ContactPageIcon} color="secondary" />

      <Box sx={{ display: "flex", position: "relative", flexGrow: 1, alignItems: "center" }}>
        <Box sx={{ width: "55%", pr: 2 }}>
          <Stack spacing={3}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    border: "2px solid white",
                    boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                  }}
                />
                <Box
                  sx={{
                    width: 2,
                    flexGrow: 1,
                    bgcolor: alpha(theme.palette.divider, 0.1),
                    my: 0.5,
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "text.secondary", fontSize: "0.65rem" }}
                >
                  CREATED DATE
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "primary.main" }}>
                  {user.createdAtStr}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "info.main",
                    border: "2px solid white",
                    boxShadow: "0 0 0 2px rgba(2, 136, 209, 0.2)",
                  }}
                />
                <Box
                  sx={{
                    width: 2,
                    flexGrow: 1,
                    bgcolor: alpha(theme.palette.divider, 0.1),
                    my: 0.5,
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "text.secondary", fontSize: "0.65rem" }}
                >
                  REGISTERED DATE
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  {user.registeredDate || "---"}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "warning.main",
                    border: "2px solid white",
                    boxShadow: "0 0 0 2px rgba(237, 108, 2, 0.2)",
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "text.secondary", fontSize: "0.65rem" }}
                >
                  MANAGED BY
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  {user.createdBy || "---"}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            width: "45%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: "24px",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "transparent",
              border: (theme) =>
                theme.palette.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.15)" : "none",
              boxShadow: (theme) =>
                theme.palette.mode === "dark" ? "0 8px 24px rgba(0, 0, 0, 0.3)" : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={calendarPastel}
              sx={{
                width: "170px",
                height: "auto",
                opacity: isDarkMode ? 0.65 : 0.5,
                mixBlendMode: isDarkMode ? "normal" : "multiply",
                filter: isDarkMode
                  ? "brightness(0.95) contrast(1.1) drop-shadow(0 10px 20px rgba(0,0,0,0.3))"
                  : `drop-shadow(0 15px 30px ${alpha(theme.palette.primary.main, 0.2)})`,
                animation: `${float} 5s ease-in-out infinite alternate`,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ContactInformation = ({ user }) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <LocationWidget user={user} />
        </Grid>
        <Grid item xs={12} md={6}>
          <AccountHistoryWidget user={user} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactInformation;
